import AddCollection from "./common/AddCollection/AddCollection";
import AddContent from "./common/AddContent/AddContent";
import { AutoCompleteDropDown } from "./common/AutoCompleteDropDown/AutoCompleteDropDown";
import ButtonField from "./common/Button/Button";
import CheckBoxComponent from "./common/CheckBox/CheckBoxComponent";
import Code from "./common/Code/Code";
import DateAndTimePicker from "./common/DateAndTimePicker/DateAndTimePicker";
import DatePickerComponent from "./common/DatePicker/DatePicker";
import { Dropdown } from "./common/DropDown/Dropdown";
import Editor from "./common/Editor/Editor";
import GallerywithOptions from "./common/GallerywithOptions/GallerywithOptions";
import IconPicker from "./common/IconPicker/IconPicker";
import ImagePicker from "./common/ImagePicker/ImagePicker";
import ImagePickerWithoutCrop from "./common/ImagePickerWithoutCrop/ImagePickerWithoutCrop";
import ImageOptions from "./common/ImageWithOptions/ImageOptions";
import ImageWithTextBox from "./common/ImageWithTextBox/ImageWithTextBox";
import PagePicker from "./common/PageListing/PagePicker";
import Query from "./common/Query/Query";
import RadioGroupButtons from "./common/RadioGroup/RadioGroupButtons";
import CustomRepeater from "./common/Repeater/CustomRepeater";
import TagListingComponent from "./common/TagListing/TagListingComponent";
import TextArea from "./common/TextArea/TextArea";
import TextBox from "./common/TextBox/TextBox";
import Toggle from "./common/Toggle/Toggle";
import VideoOptions from "./common/VideoWithOptions/VideoOptions";
import Custom1 from "./customComponents/Custom1/Custom1";
import ExcelFileUpload from "./customComponents/ExcelFileUpload/ExcelFileUpload";
import RadioCustomOptions from "./customComponents/RadioCustomOptions/RadioCustomOptions";

// This is a temporary fix to avoid cropping issue in image picker without crop. This will be removed once the issue is fixed in x-image-render package.
// eslint-disable-next-line prefer-const
let isPublishWithoutCrop = false;
const publishWithoutCrop = (check) => {
  isPublishWithoutCrop = check;
};

// Field descriptor as seen by the builder. For fields rendered inside a repeater the
// CustomRepeater also attaches the repeater coordinates (repeaterName/repeaterIndex/childName)
// so we never have to reverse-parse them out of the (underscore-containing) `name`.
interface FieldMeta {
  name?: string;
  type?: string;
  repeaterName?: string;
  repeaterIndex?: number;
  childName?: string;
  [key: string]: any;
}

const handleChange = (
  event,
  type,
  clearErrors,
  setState,
  state,
  isRepeater = false,
  field: FieldMeta | null = null,
) => {
  clearErrors(event.target.name);
  if (isRepeater) {
    //For Repeater Component inside repeater — use the coordinates carried on the
    //field object instead of reverse-parsing the name (names may contain underscores).
    const repeaterName = field?.repeaterName ?? "";
    const fieldName = field?.childName ?? "";
    const indexNum = field?.repeaterIndex ?? 0;
    const currentRepeaterArray = state?.[repeaterName] || [];
    const updatedArray = [...currentRepeaterArray];
    while (updatedArray.length <= indexNum) {
      updatedArray.push({});
    }
    updatedArray[indexNum] = {
      ...updatedArray[indexNum],
      [fieldName]: event.target.value,
    };
    setState({
      ...state,
      [repeaterName]: updatedArray,
    });
  } else if (type === "number") {
    setState({
      ...state,
      [event.target.name]: event.target.value.replace(/[^0-9]/g, ""),
    });
  } else {
    setState({
      ...state,
      [event.target.name]: event.target.value,
    });
  }
};
const handleToggleChange = (event, setState, state) => {
  //For Boolean type components
  setState({
    ...state,
    [event.target.name]: !state[event.target.name],
  });
};
const handleOnBlur = (
  event,
  type,
  setState,
  state,
  isRepeater = false,
  field: FieldMeta | null = null,
) => {
  if (isRepeater) {
    //For Repeater Component inside repeater — use the coordinates carried on the
    //field object instead of reverse-parsing the name (names may contain underscores).
    const repeaterName = field?.repeaterName ?? "";
    const fieldName = field?.childName ?? "";
    const indexNum = field?.repeaterIndex ?? 0;
    const currentRepeaterArray = state?.[repeaterName] || [];
    const updatedArray = [...currentRepeaterArray];
    while (updatedArray.length <= indexNum) {
      updatedArray.push({});
    }
    updatedArray[indexNum] = {
      ...updatedArray[indexNum],
      [fieldName]: event.target.value,
    };
    setState({
      ...state,
      [repeaterName]: updatedArray,
    });
  } else if (type === "number") {
    setState({
      ...state,
      [event.target.name]: event.target.value.replace(/[^0-9]/g, ""),
    });
  } else {
    setState({
      ...state,
      [event.target.name]: event.target.value,
    });
  }
};
// For a field rendered inside a repeater, `field.name` is the RHF-path string
// (e.g. `past_champion_entries[0].champion_image_past_champion_entries_93709`)
// — not a real state key. Writing it straight into `state` (as this used to do)
// leaks a stray top-level key instead of updating the array item, and the
// backend then rejects the payload as an unknown field. Use the repeater
// coordinates carried on the field object instead, mirroring handleChange's
// repeater branch above.
const setImageFieldValue =
  (imageValue, isRepeater, field: FieldMeta | null) => (clearErrors, setState, state) => {
    if (isRepeater) {
      const repeaterName = field?.repeaterName ?? "";
      const fieldName = field?.childName ?? "";
      const indexNum = field?.repeaterIndex ?? 0;
      clearErrors(fieldName);
      const currentRepeaterArray = state?.[repeaterName] || [];
      const updatedArray = [...currentRepeaterArray];
      while (updatedArray.length <= indexNum) {
        updatedArray.push({});
      }
      updatedArray[indexNum] = {
        ...updatedArray[indexNum],
        [fieldName]: imageValue,
      };
      setState({
        ...state,
        [repeaterName]: updatedArray,
      });
      return;
    }
    const name = field?.name ?? "";
    clearErrors(name);
    setState({
      ...state,
      [name]: {
        ...state[name],
        ...imageValue,
      },
    });
  };
const updateField =
  (updatedPartialObj, isRepeater = false, field: FieldMeta | null = null) =>
  (clearErrors, setState, state) =>
    setImageFieldValue(
      {
        original_image: updatedPartialObj?.original_image,
        published_images: updatedPartialObj?.published_images,
      },
      isRepeater,
      field,
    )(clearErrors, setState, state);
const updateImageFieldWithoutCrop =
  (updatedPartialObj, isRepeater = false, field: FieldMeta | null = null) =>
  (clearErrors, setState, state) =>
    setImageFieldValue(
      {
        original_image: {
          ...updatedPartialObj?.original_image,
          use_original_crop: isPublishWithoutCrop,
        },
        published_images: updatedPartialObj?.published_images,
      },
      isRepeater,
      field,
    )(clearErrors, setState, state);
const handleChangeOn = (newValue, field, multiple) => (clearErrors, setState, state, fields) => {
  clearErrors(field?.name);
  const subTypeName = fields?.filter((value) => {
    return value.dependent_name === field?.name;
  });
  if (subTypeName.length > 0) {
    setState({
      ...state,
      [field.name]: multiple ? newValue : [newValue],
      [subTypeName[0]?.name]: [],
    });
  } else {
    setState({
      ...state,
      [field.name]: multiple ? newValue : [newValue],
    });
  }
};
const updateState =
  (name, value, isRepeater = false, field: FieldMeta | null = null) =>
  (clearErrors, setState, state) => {
    //For TagListing Component
    clearErrors(name);
    if (isRepeater) {
      //For a field rendered inside a repeater — use the coordinates carried on the
      //field object instead of reverse-parsing the name (names may contain underscores),
      //mirroring handleChange's repeater branch above.
      const repeaterName = field?.repeaterName ?? "";
      const fieldName = field?.childName ?? "";
      const indexNum = field?.repeaterIndex ?? 0;
      const currentRepeaterArray = state?.[repeaterName] || [];
      const updatedArray = [...currentRepeaterArray];
      while (updatedArray.length <= indexNum) {
        updatedArray.push({});
      }
      updatedArray[indexNum] = {
        ...updatedArray[indexNum],
        [fieldName]: value,
      };
      setState({
        ...state,
        [repeaterName]: updatedArray,
      });
      return;
    }
    setState({
      ...state,
      [name]: value,
    });
  };

// const updateAssetWithText = (updatedPartialObj, name) => (clearErrors, setState, state) => {
//   //For ImagePicker Component
//   console.log("shadowUpdated", updatedPartialObj);
//   clearErrors(name);
//   setState({
//     ...state,
//     [name]: {
//       ...state[name],
//       original_image: updatedPartialObj?.original_image,
//       published_images: updatedPartialObj?.published_images,
//     },
//   });
// };

const optionCallBack = (field, state) => {
  //For Dynamic Dropdown
  if (state?.[field?.dependent_name]) {
    return field?.options?.filter((item) =>
      state?.[field?.dependent_name]?.map((x) => x.id).includes(item.parentId),
    );
  } else {
    return field?.options;
  }
};

const errorDestructure = (errors, fieldName, isRepeater = false) => {
  if (isRepeater) {
    const nameParts = fieldName?.split(/[\[\]\.]+/)?.filter(Boolean);
    const childFieldName = nameParts?.slice(2).join(".");
    return errors?.[childFieldName];
  }
  return errors?.[fieldName];
};

const dataDestructure = (state, field, isRepeater = false) => {
  if (isRepeater) {
    //Read the value using the coordinates carried on the field object instead of
    //reverse-parsing the name (names may contain underscores, e.g. `dam_key_name`).
    const repeaterName = field?.repeaterName;
    const indexNum = field?.repeaterIndex;
    const childFieldName = field?.childName;
    const currentItem = state?.[repeaterName]?.[indexNum];
    if (!currentItem) {
      return "";
    }
    return currentItem[childFieldName] || "";
  }
  return state?.[field?.name] || "";
};

function builder(
  field,
  state,
  errors,
  register,
  clearErrors,
  setState,
  fields,
  isEdit,
  title,
  control,
  callbackEvents,
  isRepeater = false,
) {
  switch (field?.type) {
    case "text":
      switch (field.variant) {
        case "multiline":
          return (
            <TextArea
              field={field}
              errors={errors}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }
            />
          );
        case "editor":
          return (
            <Editor
              field={field}
              errors={errors}
              control={control}
              data={dataDestructure(state, field, isRepeater)}
              title={state["title"] || ""}
              updateState={(name, value) =>
                updateState(name, value, isRepeater, field)(clearErrors, setState, state)
              }
            />
          );
        case "local_upload": //excel file upload from local from customComponents
          return (
            <ExcelFileUpload
              item={field}
              control={control}
              data={state[field?.name]}
              updateState={(name, value) => updateState(name, value)(clearErrors, setState, state)}
            />
          );
        case "number":
          return (
            <TextBox
              type='number'
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }></TextBox>
          );
        case "code":
          return (
            <Code
              field={field}
              state={state}
              setState={setState}
              control={control}
              clearErrors={clearErrors}
              error={errorDestructure(errors, field.name, isRepeater)}
              data={dataDestructure(state, field, isRepeater)}
              isDisabled={false}
              isRepeater={isRepeater}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
            />
          );
        default:
          return (
            <TextBox
              type='text'
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              clearErrors={clearErrors}
              data={dataDestructure(state, field, isRepeater)}
              handleChange={(event) =>
                handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
              }
              handleOnBlur={(event) =>
                handleOnBlur(event, field?.type, setState, state, isRepeater, field)
              }></TextBox>
          );
      }
    case "img_gallery":
      switch (field?.variant) {
        case "no_crop":
          return <ImageOptions setState={setState} state={state} field={field} variant='no_crop' />;
        default:
          return (
            <ImageOptions
              setState={setState}
              state={state}
              field={field}

              // updateAssetWithText={(updatedPartialObj, name) =>
              //   updateAssetWithText(updatedPartialObj, name)(clearErrors, setState, state)
              // }
            />
          );
      }
    case "vid_gallery":
      return <VideoOptions setState={setState} state={state} field={field} />;
    case "gallery":
      return <GallerywithOptions setState={setState} state={state} field={field} />;
    case "image":
      switch (field?.variant) {
        case "no_crop":
          return (
            <ImagePickerWithoutCrop
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateImageFieldWithoutCrop(updatedPartialObj, isRepeater, field)(
                  clearErrors,
                  setState,
                  state,
                )
              }
              containerSize={{ width: "100%", height: "192px" }}
              aspect_ratio={field?.aspect_ratio}
              publishWithoutCrop={publishWithoutCrop}
              variant='nocrop'
            />
          );
        case "no_crop_variant_2":
          return (
            <ImagePickerWithoutCrop
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateImageFieldWithoutCrop(updatedPartialObj, isRepeater, field)(
                  clearErrors,
                  setState,
                  state,
                )
              }
              containerSize={{ width: "100%", height: "192px" }}
              aspect_ratio={field?.aspect_ratio}
              publishWithoutCrop={publishWithoutCrop}
              variant='nocrop'
            />
          );
        case "no_crop_variant_3":
          return (
            <ImagePickerWithoutCrop
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateImageFieldWithoutCrop(updatedPartialObj, isRepeater, field)(
                  clearErrors,
                  setState,
                  state,
                )
              }
              containerSize={{ width: "50px", height: "50px" }}
              aspect_ratio={field?.aspect_ratio}
              publishWithoutCrop={publishWithoutCrop}
              variant='nocrop'
            />
          );
        default:
          return (
            <ImagePicker
              field={field}
              error={errorDestructure(errors, field?.name, isRepeater)}
              register={register}
              data={dataDestructure(state, field, isRepeater)}
              updateField={(updatedPartialObj) =>
                updateField(updatedPartialObj, isRepeater, field)(clearErrors, setState, state)
              }
            />
          );
      }

    case "content":
      switch (field?.variant) {
        case "contentType":
          return (
            <AddContent
              state={state}
              setState={setState}
              field={field}
              error={errors[field.name]}
              register={register}
              clearErrors={clearErrors}
            />
          );
        case "collection":
          return (
            <AddCollection
              state={state}
              setState={setState}
              field={field}
              error={errors[field.name]}
              register={register}
              clearErrors={clearErrors}
            />
          );
        case "page":
          return <PagePicker state={state} setState={setState} field={field} />;
        default:
          return null;
      }

    // return (
    //   <ContentTypePicker
    //     field={field}
    //     errors={errors}
    //     register={register}
    //     state={state}
    //     updateField={() => {}}
    //   />
    // );
    case "query":
      return (
        <Query
          field={field}
          state={state}
          errors={errors}
          setState={setState}
          register={register}
          isEdit={isEdit}
          clearErrors={clearErrors}
        />
      );
    case "date":
      return (
        <DatePickerComponent
          disablePast={field?.disable_past}
          state={state}
          setState={setState}
          field={field}
          register={register}
          clearErrors={clearErrors}
          error={errors[field.name]}
          control={control}
        />
      );
    case "radio":
      return (
        <RadioGroupButtons
          value={field?.options}
          field={field}
          row={true}
          handleChange={(event) => handleChange(event, field?.type, clearErrors, setState, state)}
          error={errors[field.name]}
          control={control}
          clearErrors={clearErrors}
          data={state[field.name]}
        />
      );
    case "dropdown":
      return field?.variant === "searchdropdown" ? (
        <AutoCompleteDropDown
          options={field?.dependent_name ? optionCallBack(field, state) : field?.options}
          state={state}
          field={field}
          optionFormat='label'
          error={errors[field.name]}
          register={register}
          handleChangeOn={(newValue, currentField, multiple) =>
            handleChangeOn(newValue, currentField, multiple)(clearErrors, setState, state, fields)
          }
        />
      ) : (
        <Dropdown
          options={field?.options}
          setState={setState}
          state={state}
          error={errors[field.name]}
          register={register}
          field={field}
          clearErrors={clearErrors}
          callbackEvents={callbackEvents}
          control={control}
        />
      );
    // case "checkbox":
    //   return (
    //     <CheckBoxComponent
    //       field={field}
    //       error={errors[field.name]}
    //       register={register}
    //       handleChange={handleToggleChange}
    //       checked={state[field?.name]}
    //     />
    //   );
    case "boolean":
      switch (field.variant) {
        case "toggle":
          return (
            <Toggle
              field={field}
              error={errors[field.name]}
              disabled={false}
              color={"#000000"}
              onChange={(event) => handleToggleChange(event, setState, state)}
              checked={state[field?.name]}
              name={field?.name}
              register={register}
            />
          );
        case "checkbox":
          return (
            <CheckBoxComponent
              field={field}
              error={errors[field.name]}
              register={register}
              handleChange={(event) => handleToggleChange(event, setState, state)}
              checked={state[field?.name]}
            />
          );
        default:
          return null;
      }
    case "tag":
      return (
        Object.keys(state).length > 0 && (
          <TagListingComponent
            field={field}
            isEdit={isEdit}
            selectedTag={field?.name === "tag_sm" ? state["tags"] : state[field?.name]}
            subCategory={title}
            updateState={(name, value) => updateState(name, value)(clearErrors, setState, state)}
            register={register}
            error={errors[field.name]}
            setState={setState}
            state={state}
          />
        )
      );
    case "datetime":
      return (
        <DateAndTimePicker
          disablePast={field?.disable_past}
          field={field}
          error={errors[field.name]}
          state={state}
          updateState={(name, value) => updateState(name, value)(clearErrors, setState, state)}
          clearErrors={clearErrors}
          control={control}
        />
      );
    case "custom1": //Challenge Rule // This is a sample component developed for healthcare.
      return (
        <Custom1
          type='text'
          field={field}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          data={state[field?.name]}
          handleChange={(event) => handleChange(event, field?.type, clearErrors, setState, state)}
          handleOnBlur={(event) => handleOnBlur(event, field?.type, setState, state)}
        />
      );
    case "radioCustomOptions":
      return (
        <RadioCustomOptions
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          control={control}
          fields={fields}
          isEdit={isEdit}
          title={title}
        />
      );
    case "repeater":
      return (
        <CustomRepeater
          field={field}
          control={control}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          state={state}
          setState={setState}
          isEdit={isEdit}
          title={title}
        />
      );
    case "imagewithtext":
      return (
        <ImageWithTextBox
          field={field}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
          state={state}
          updateField={(updatedPartialObj) =>
            updateImageFieldWithoutCrop(updatedPartialObj, isRepeater, field)(
              clearErrors,
              setState,
              state,
            )
          }
          handleChange={(event) =>
            handleChange(event, field?.type, clearErrors, setState, state, isRepeater, field)
          }
          handleOnBlur={(event) =>
            handleOnBlur(event, field?.type, setState, state, isRepeater, field)
          }
          publishWithoutCrop={publishWithoutCrop}
          containerSize={{ width: "50px", height: "50px" }}
        />
      );
    case "button":
      return (
        <ButtonField
          field={field}
          control={control}
          state={state[field?.name]}
          setState={(value) => {
            clearErrors(field?.name);
            setState({
              ...state,
              [field?.name]: value,
            });
          }}
          isDisabled={false}
        />
      );
    case "icon":
      return (
        <IconPicker
          field={field}
          state={state}
          setState={setState}
          errors={errors}
          register={register}
          clearErrors={clearErrors}
        />
      );
    default:
      return null;
  }
}

export default builder;
